import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { getAllPurposes } from "../../services/purposes";
import { createSubmission } from "../../services/submissions";
import { CustomSelect } from "./CustomSelect";
import FormActions from "./FormActions";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { Button } from "./Button";

const PURPOSE_PAGE_SIZE = 10;

export default function RegisterComplaintModal({
  tenantId,
  companyName,
  onClose,
  onSuccess,
}) {
  const isMobile = useIsMobile();
  const [purposes, setPurposes] = useState([]);
  const [purposePage, setPurposePage] = useState(1);
  const [purposeHasMore, setPurposeHasMore] = useState(true);
  const [purposeLoading, setPurposeLoading] = useState(false);
  const [selectedPurpose, setSelectedPurpose] = useState("");
  const [sendToCompany, setSendToCompany] = useState(false);
  const [sendToDpo, setSendToDpo] = useState(false);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const loadPurposes = useCallback(
    (page) => {
      if (!tenantId) return;
      setPurposeLoading(true);
      getAllPurposes(tenantId, { page, page_size: PURPOSE_PAGE_SIZE })
        .then((res) => {
          const data = res.data?.data || {};
          const items = data.items || [];
          setPurposes((prev) => (page === 1 ? items : [...prev, ...items]));
          setPurposeHasMore(page < (data.total_pages || 0));
          setPurposePage(page);
        })
        .catch(() => {
          if (page === 1) setPurposes([]);
          setPurposeHasMore(false);
        })
        .finally(() => setPurposeLoading(false));
    },
    [tenantId],
  );

  useEffect(() => {
    loadPurposes(1);
  }, [loadPurposes]);

  const loadMorePurposes = () => {
    if (purposeLoading || !purposeHasMore) return;
    loadPurposes(purposePage + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createSubmission(tenantId, selectedPurpose || undefined, {
        submission_type: "complaint",
        description,
        send_to_company: sendToCompany,
        send_to_dpo: sendToDpo,
      });
      toast.success("Complaint registered successfully!");
      onSuccess?.();
      onClose();
    } catch {
      toast.error("Failed to register complaint. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 md:flex md:items-center md:justify-center z-50 md:p-4">
      <div className="bg-white md:rounded-2xl shadow-xl w-full h-full md:h-auto md:max-w-lg md:max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100 bg-gray-50 md:bg-white sticky top-0 md:static">
          {isMobile ? (
            <Button
              variant="tertiary"
              onClick={onClose}
              className="!text-gray-700 !p-1 -ml-1"
              aria-label="Back"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </Button>
          ) : (
            <h2 className="text-lg font-semibold text-gray-800">
              Register a Complaint
            </h2>
          )}
          {isMobile && (
            <span className="text-base font-semibold text-gray-800">
              Requests &amp; Complaints
            </span>
          )}
          {isMobile ? (
            <span className="w-[22px]" />
          ) : (
            <Button
              variant="tertiary"
              onClick={onClose}
              className="!text-gray-400 hover:!text-gray-600"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </Button>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-4 md:p-6 pb-24 md:pb-6 space-y-5 flex flex-col md:block min-h-[calc(100%-57px)] md:min-h-0"
        >
          {/* Select Purpose */}
          <div className="hidden md:block">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Purpose{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <CustomSelect
              value={selectedPurpose}
              onChange={setSelectedPurpose}
              placeholder="Select the purpose to register a complaint"
              options={purposes.map((p) => ({
                value: p.purpose_id ?? p.purposeId ?? p.id,
                label: p.purpose ?? p.name,
              }))}
              onLoadMore={loadMorePurposes}
              hasMore={purposeHasMore}
              loadingMore={purposeLoading}
              searchable
            />
          </div>

          {/* Send to */}
          {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Send complaint to</label>
              <div className="flex gap-8">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendToCompany}
                    onChange={(e) => setSendToCompany(e.target.checked)}
                    className="w-4 h-4 rounded accent-teal-600"
                  />
                  <span className="text-sm text-gray-700">{companyName}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendToDpo}
                    onChange={(e) => setSendToDpo(e.target.checked)}
                    className="w-4 h-4 rounded accent-teal-600"
                  />
                  <span className="text-sm text-gray-700">Data Protection Officer</span>
                </label>
              </div>
            </div> */}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Describe your complaint <span className="text-[#C73838]">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={6}
              placeholder="e.g. I withdrew my consent for marketing communications on 10 June 2026 but continue to receive promotional emails."
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
            />
          </div>

          {/* <p className="text-sm text-gray-600">
            Complaint will be sent to{" "}
            <a
              href="mailto:dpb@gov.in"
              className="text-blue-600 hover:underline"
            >
              dpb@gov.in
            </a>
            .
          </p> */}

          <div className="flex-1 md:hidden" />

          <p className="text-sm text-gray-600">
            Register a complaint with the Data Protection Board of India here -{" "}
            <a
              href="mailto:dpb@gov.in"
              className="text-blue-600 hover:underline"
            >
              dpb@gov.in
            </a>
            .
          </p>

          <FormActions
            onCancel={onClose}
            loading={loading}
            submitLabel="Register Complaint"
            loadingLabel="Registering..."
            submitDisabled={loading || !description.trim()}
          />
        </form>
      </div>
    </div>
  );
}
